import { ChangeDetectorRef, Component, HostListener, Input, OnInit, OnDestroy } from '@angular/core';
import { DisplayService } from 'src/app/services/display.service';
import { EditFeatureService } from '../query-views/services/edit-feature.service';
import VectorImageLayer from 'ol/layer/VectorImage';
import { MatSelectChange } from '@angular/material/select';
import { Subscription, forkJoin } from 'rxjs';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AppText, EditableGeometryInfo, EditableSubGeometryInfo, Field, FieldTypes, GeometryTypes, LayerType, User, UserAccess, UserArea, UserGroupInfo, UserInGroup } from 'src/app/interfaces/common';
import { UserService } from 'src/app/services/user.service';
import { Collection, Feature } from 'ol';
import { Geometry, LineString, MultiLineString, Polygon } from 'ol/geom';
import { LayerService } from 'src/app/services/layer.service';
import { HttpClient } from '@angular/common/http';
import { GeoJSON, GPX, WKT } from 'ol/format';
import { Extent } from 'ol/extent';
import { DragAndDrop, Modify, Translate } from 'ol/interaction';
import { environment } from 'src/environments/environment';
import { switchMap, tap } from 'rxjs/operators';
import { unByKey } from 'ol/Observable';
import VectorLayer from 'ol/layer/Vector';
import { DragAndDropEvent } from 'ol/interaction/DragAndDrop';
import { IEntity } from 'dxf-parser';
import { LayoutService } from 'src/app/services/layout.service';
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import VectorSource from 'ol/source/Vector';

interface LineEntity extends IEntity {
  startPoint: { x: number; y: number };
  endPoint: { x: number; y: number };
}

interface PolylineEntity extends IEntity {
  vertices: { x: number; y: number }[];
  closed?: boolean;
}

interface CircleEntity extends IEntity {
  center: { x: number; y: number };
}

interface ArcEntity extends IEntity {
  center: { x: number; y: number };
  radius: number;
  startAngle: number;
  endAngle: number;
}

interface PointEntity extends IEntity {
  position: { x: number; y: number };
}

enum SupportedFileType {
  DXF = '.dxf',
  XLSX = '.xlsx'
}

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.css']
})
export class CreateComponent implements OnInit, OnDestroy {
  @Input() selText: AppText = {};
  @Input() applicationSettings: any;
  FieldTypes = FieldTypes;
  UserAccess = UserAccess;
  allowCreate: boolean = false;
  isAdminCreatingRestrictedArea: boolean = false;
  restrictedAreaName: string = "";
  allowedGeoTypes: EditableGeometryInfo[] = [];
  allowFilter: boolean = false;
  filterColumn: string = "";
  areasSubscription: Subscription | null = null;
  isAdminCreatingUser: boolean = false;
  isAdminEditingUsers: boolean = false;
  isAdminEditingAreas: boolean = false;
  userForm: FormGroup | null = null;
  fields: Field[] = [];
  sortedFields: Field[] = [];
  filteredAreas: UserArea[] = [];
  areaSearchTerm: string = "";
  users: User[] = [];
  sortedUsers: User[] = [];
  sortedNonFilteredUsers: User[] = [];
  selectedUser: User | null = null;
  originalUser: User | null = null;
  allowedGroups: string[] = [];
  allowedGroupsWithIds: UserGroupInfo[] = [];
  drawAreaTranslateInteraction: Translate | null = null;
  drawAreaModifyInteraction: Modify | null = null;
  selectedArea: UserArea | null = null;
  originalAreaGeometry: Geometry | null = null;
  hasAreaChanged: boolean = false;
  newAreaName: string = "";
  hasUserChanged: boolean = false;
  isFilteringUsers: boolean = false;
  haveRestrictedArea: boolean = false;
  passwordFieldType: string = 'password';
  newPasswordFieldType: string = 'password';
  snapOnItems: boolean = false;
  userAccess = UserAccess.None;
  haveDragAndDropFeatures: boolean = false;

  constructor(
    private http: HttpClient,
    public displayService: DisplayService,
    public editFeatureService: EditFeatureService,
    public layerService: LayerService,
    private userService: UserService,
    public layoutService: LayoutService,
    private changeDetector: ChangeDetectorRef
  ) { }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.editFeatureService.isDrawingFeature) {
      this.cancelNewFeature();
    }
    else if (this.isAdminCreatingRestrictedArea) {
      const pointerEvent = new PointerEvent('pointerdown', { pointerType: 'mouse' });
      this.cancelNewRestrictedArea(pointerEvent);
    }
  }

  ngOnInit(): void {
    this.userAccess = this.userService.getUserAccess();
    const allowCreate = this.displayService.getAllowCreate();
    if (allowCreate) {
      this.allowCreate = allowCreate;
      this.allowedGeoTypes = this.getAllowedGeoTypes();
    }
    const [allowFilter, filterColumn] = this.displayService.getFilterInfo();
    this.allowFilter = allowFilter;
    this.filterColumn = filterColumn;
    this.filteredAreas = [...this.editFeatureService.allAreas$.getValue()];
    this.getAllowedGroups();
    if (this.applicationSettings.option?.edit?.restrictedArea) {
      this.haveRestrictedArea = true;
      if (this.userService.userAccess >= UserAccess.Admin) {
        this.getUsers();
      }
    }
    this.snapOnItems = this.editFeatureService.getSnapOnItems();

    this.setupOlDragAndDropInteraction();
    if (this.applicationSettings.option?.customDragAndDropFileTypes?.length) {
      this.setupManualDragAndDrop();
    }
  }

  ngOnDestroy(): void {
    this.areasSubscription?.unsubscribe();
  }

  cancelNewFeature() {
    this.displayService.allowFeatureClick();
    this.editFeatureService.cancelNewFeature();
  }

  clearDragAndDropFeatures() {
    this.editFeatureService.clearDragAndDropFeatures();
    this.haveDragAndDropFeatures = false;
  }

  private setupOlDragAndDropInteraction(): void {
    const dragAndDropInteraction = new DragAndDrop({
      formatConstructors: [new GPX(), GeoJSON]
    });

    dragAndDropInteraction.on('addfeatures', (event: DragAndDropEvent) => {
      this.layerService.clearDrawingLayerSource(this.editFeatureService.mapObject);
      if (!event.features?.length) return;

      const features = event.features as Feature<Geometry>[];
      const fileName: string = (event as any).file?.name ?? 'Dropped file';

      features.forEach(f => {
        this.editFeatureService.convertFeatureGeometry(f);
        f.set('isDragAndDrop', true);
      });

      const newSource = new VectorSource({ features });
      const newLayer = new VectorLayer({
        source: newSource,
        style: this.layerService.getDefaultVectorDragAndDropDrawStyles() as any,
        zIndex: 1
      });

      const dragAndDropLayers = this.layerService.mapLayers.filter(ml => ml.type === LayerType.DragAndDrop);

      const alreadyExists = dragAndDropLayers.some(ml => ml.name === fileName);

      if (alreadyExists) {
        this.displayService.toastrShowWarning({
          message: `File "${fileName}" has already been added.`,
          tapToDismiss: true,
          closeButton: true
        });
        return;
      }

      this.layerService.addDragAndDropLayer(fileName, newLayer);

      if (this.userService.userAccess >= UserAccess.Update) this.editFeatureService.selectedInteractionLayers.push(newLayer);

      this.editFeatureService.dragAndDropFileDropped$.next(); // Notify that a file was dropped
      this.haveDragAndDropFeatures = true;
      this.changeDetector.markForCheck();

      // Fit the view to the new feature if only one is added, otherwise pan to the first inserted feature
      if (features.length === 1) {
        const extent = newSource.getExtent();
        if (extent) {
          this.editFeatureService.mapObject?.getView().fit(extent, {
            maxZoom: 10
          });
        }
      }
      else this.displayService.panAndZoomToFeature(features[0], 10);

      this.editFeatureService.mapObject?.addInteraction(dragAndDropInteraction);
    });

    this.editFeatureService.mapObject?.addInteraction(dragAndDropInteraction);
  }

  private setupManualDragAndDrop(): void {
    const mapViewport = this.editFeatureService.mapObject?.getViewport();
    if (!mapViewport) return;

    const supportedFileTypes: SupportedFileType[] = this.applicationSettings.option?.customDragAndDropFileTypes;
    if (supportedFileTypes.length < 1) return;

    mapViewport.addEventListener('drop', (event: DragEvent) => {
      event.preventDefault();

      const file = event.dataTransfer?.files?.[0];
      if (!file) return;

      const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase() as SupportedFileType;
      if (!supportedFileTypes.includes(fileExtension)) return;  // Only allow file extensions that are explicitly configured

      switch (fileExtension) {
        case SupportedFileType.DXF:
          this.handleDxfFile(file);
          break;
        case SupportedFileType.XLSX:
          this.handleXlsxFile(file);
          break;
      }
    });
  }

  private convertXlsxToFeatureCollection(data: any[]): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];

    data.forEach((row, index) => {
      if (index === 0 || row.length < 3) return;  // Skip headers and invalid rows

      const label = row[0];
      const name = row[1];
      let x: number = NaN;
      let y: number = NaN;

      if (typeof row[2] === "string" && row[2].includes(" ")) {
        // Single-column format (e.g., "445387.23 389243.027")
        const [xStr, yStr] = row[2].split(" ");
        x = parseFloat(xStr);
        y = parseFloat(yStr);
      } else if (row.length >= 4) {
        // Separate-column format (X in row[2], Y in row[3])
        x = parseFloat(row[2]);
        y = parseFloat(row[3]);
      }

      //console.log(`Parsed values - Name: ${name}, X: ${x}, Y: ${y}`);

      if (!isNaN(x) && !isNaN(y)) {
        const feature: GeoJSON.Feature = {
          type: "Feature",
          geometry: {
            type: GeometryTypes.Point,
            coordinates: [x, y]
          },
          properties: {}
        };

        features.push({
          type: 'Feature',
          geometry: feature.geometry,
          properties: {
            isDragAndDrop: true,
            name: name,
            label: label
          }
        });
      }
    });

    //console.log("Generated GeoJSON:", features);
    return { type: "FeatureCollection", features };
  }

  private convertDxfEntitiesToFeatureCollection(entities: IEntity[]): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];

    for (const entity of entities) {
      let geometry: GeoJSON.Geometry | null = null;

      switch (entity.type) {
        case 'LINE':  // Represents a straight line between two points(startPoint and endPoint)
          {
            const { startPoint, endPoint } = entity as LineEntity;
            if (startPoint && endPoint) {
              geometry = { type: GeometryTypes.LineString, coordinates: [[startPoint.x, startPoint.y], [endPoint.x, endPoint.y]] };
          }
          break;
          }
        case 'LWPOLYLINE':  // Represents a series of connected line segments that can form a polygon or polyline
        case 'POLYLINE':    // A polyline entity can contain multiple vertices and be either open or closed
          {
            const { vertices, closed } = entity as PolylineEntity;
            if (vertices && vertices.length > 0) {
              geometry = closed
                ? { type: GeometryTypes.Polygon, coordinates: [[...vertices.map(v => [v.x, v.y]), [vertices[0].x, vertices[0].y]]] }
                : { type: GeometryTypes.LineString, coordinates: vertices.map(v => [v.x, v.y]) };
            }
            break;
          }
        case 'CIRCLE':  // Represents a circle with a specified center and radius
          {
            const { center } = entity as CircleEntity;
            if (center) {
              geometry = { type: GeometryTypes.Point, coordinates: [center.x, center.y] };
            }
            break;
          }
        case 'ARC': // A portion of a circle, defined by a center, radius, start angle, and end angle
          {
            const { center, radius, startAngle, endAngle } = entity as ArcEntity;
            if (center && radius && startAngle !== undefined && endAngle !== undefined) {
              const numSegments = 36; // Number of segments to approximate the arc
              const coordinates = Array.from({ length: numSegments + 1 }, (_, i) => {
                const angle = startAngle + (i * (endAngle - startAngle)) / numSegments;
                return [center.x + radius * Math.cos(angle), center.y + radius * Math.sin(angle)];
              });
              geometry = { type: GeometryTypes.LineString, coordinates };
            }
            break;
          }
        case 'POINT': {
          const { position } = entity as PointEntity;
          if (position) {
            geometry = { type: GeometryTypes.Point, coordinates: [position.x, position.y] };
          }
          break;
        }
        case 'SOLID':
        case 'ELLIPSE':
        case 'SPLINE':
        default:
          continue;
      }

      if (geometry) {
        features.push({
          type: 'Feature',
          geometry: geometry,
          // Add the layer text from the .dxf file to the comment field if it's configured as 'useNameFromGeoFile'
          properties: { isDragAndDrop: true, name: entity.layer }
        });
      }
    }

    return { type: 'FeatureCollection', features };
  }

  onGroupRadioChange(selectedGroup: UserGroupInfo): void {
    this.selectedUser?.groups.forEach(group => {
      group.selected = (group === selectedGroup);
    });
  }

  private addFeaturesToMap(geoJsonFeatures: GeoJSON.FeatureCollection, fileName: string): void {
    const features = new GeoJSON().readFeatures(geoJsonFeatures);

    if (features.length === 0) {
      console.warn("No valid features to add from GeoJSON.");
      return;
    }

    const dragAndDropLayers = this.layerService.mapLayers.filter(ml => ml.type === LayerType.DragAndDrop);
    const alreadyExists = dragAndDropLayers.some(ml => ml.name === fileName);
    if (alreadyExists) {
      this.displayService.toastrShowWarning({
        message: `File "${fileName}" has already been added.`,
        tapToDismiss: true,
        closeButton: true
      });
      return;
    }

    features.forEach(f => {
      f.set('isDragAndDrop', true);
    });

    const newSource = new VectorSource({ features });
    const newLayer = new VectorLayer({
      source: newSource,
      zIndex: 1
    });

    this.layerService.addDragAndDropLayer(fileName, newLayer);

    if (this.userService.userAccess >= UserAccess.Update) this.editFeatureService.selectedInteractionLayers.push(newLayer);

    // Refresh snaps so the new layer's features are snappable
    if (this.editFeatureService.getSnapTurnedOn()) {
      this.editFeatureService.setSnapToFeaturesAndRestrictedAreas();
      this.editFeatureService.setSnapToDragAndDropFeatures();
    }

    this.editFeatureService.dragAndDropFileDropped$.next();
    this.haveDragAndDropFeatures = true;

    if (features.length === 1) {
      const extent = newSource.getExtent();
      if (extent) this.editFeatureService.mapObject?.getView().fit(extent, { maxZoom: 10 });
    }
    else this.displayService.panAndZoomToFeature(features[0], 10);

    this.changeDetector.markForCheck();
  }

  private handleXlsxFile(file: File): void {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target?.result as ArrayBuffer);
      const { read, utils } = await import('xlsx');
      const workbook = read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData: any[] = utils.sheet_to_json(sheet, { header: 1 });
      const features = this.convertXlsxToFeatureCollection(jsonData);
      if (features) this.addFeaturesToMap(features, file.name);
    };
    reader.readAsArrayBuffer(file);
  }

  private handleDxfFile(file: File): void {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dxfContent = e.target?.result as string;
      const { default: DxfParser } = await import('dxf-parser');
      const parser = new DxfParser();
      const dxf = parser.parseSync(dxfContent);
      if (dxf?.entities) {
        const features = this.convertDxfEntitiesToFeatureCollection(dxf.entities);
        if (features) this.addFeaturesToMap(features, file.name);
      }
    };
    reader.readAsText(file);
  }

  private getAllowedGeoTypes(): EditableGeometryInfo[] {
    const allowedGeoTypes: EditableGeometryInfo[] = [];
    this.editFeatureService.mapLayers.forEach(mapLayer => {
      if (mapLayer.option?.mp_layer?.edit?.editable && mapLayer.option?.mp_layer?.edit?.geometry?.type) {
        const allowedGeoType: EditableGeometryInfo = {
          Type: mapLayer.option.mp_layer.edit.geometry.type
        }
        if (mapLayer.option?.mp_layer?.edit?.geometry?.subType?.label && mapLayer.option?.mp_layer?.edit?.geometry?.subType?.layer) {
          const subType: EditableSubGeometryInfo = {
            Label: mapLayer.option.mp_layer.edit.geometry.subType?.label,
            Layer: mapLayer.option.mp_layer.edit.geometry.subType.layer
          }
          allowedGeoType.SubType = subType;
        }
        // When skipLayerSelection is true, skip entries whose geometry type is already represented
        const skipLayerSelection: boolean = this.applicationSettings?.option?.edit?.skipLayerSelection === true;
        if (skipLayerSelection && allowedGeoTypes.some(g => g.Type === allowedGeoType.Type)) return;
        allowedGeoTypes.push(allowedGeoType);
      }
    });
    return allowedGeoTypes;
  }

  togglePasswordVisibility(isNewPassword: boolean): void {
    if (isNewPassword) {
      this.newPasswordFieldType = this.newPasswordFieldType === 'password' ? 'text' : 'password';
    }
    else {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    }
  }

  toggleSnapInteraction(): void {
    const snapOn = this.editFeatureService.getSnapTurnedOn();

    if (snapOn) {
      this.editFeatureService.removeAllSnaps();
    } else {
      this.editFeatureService.setSnapToArea();
      this.editFeatureService.setSnapToFeatures();
      this.editFeatureService.setSnapToDragAndDropFeatures();
    }
  }

  /**
   * Handles the change event of the slide toggle for filtering by creator
   */
  onSlideToggleChange(): void {
    if (this.editFeatureService.isFilterTextBoxVisible) this.editFeatureService.clearInteractions(true, false);

    this.displayService.setMultipleActiveStates(false, false, false, false);

    this.editFeatureService.mapObject?.getAllLayers()?.forEach(layer => {
      const source = layer.getSource?.();
      const isRefreshable = (layer instanceof WebGLVectorLayer || layer instanceof VectorImageLayer) && layer.get('layerRole') !== 'drawing';
      if (isRefreshable) source?.refresh();
    });
  }

  filterAreas(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;  // Cast event.target to HTMLInputElement to access the value property

    if (target) {
      this.areaSearchTerm = target.value.toLowerCase();
      this.filteredAreas = this.editFeatureService.allAreas$.getValue().filter(
        (area: UserArea) => area.combined.toLowerCase().includes(this.areaSearchTerm)
      );
    }
  }

  drawNewFeature(featureType: string, layer?: string): void {
    this.cancelNewFeature();
    this.editFeatureService.isDrawingFeature = true;  // TODO: Is this needed as it is set to true in drawNewFeature()?
    this.displayService.disallowFeatureClick();
    this.editFeatureService.selectInteraction?.setActive(false);
    this.displayService.setMultipleActiveStates(null, false, false, false);
    this.layoutService.updateMainActionViewWidthToInitState();
    this.editFeatureService.featureType = featureType;

    if (layer) this.editFeatureService.featureLayer = layer;
    else this.editFeatureService.featureLayer = "";
    
    this.editFeatureService.drawNewFeature();
  }

  drawNewRestrictedArea(isRectangle: boolean): void {
    this.editFeatureService.isDrawingRestrictedArea$.next(true);
    this.displayService.setMultipleActiveStates(false, false, false, false);
    this.layoutService.updateMainActionViewWidthToInitState();
    this.isAdminCreatingRestrictedArea = true;
    this.editFeatureService.drawNewRestrictedArea(isRectangle);
  }

  addNewUser(): void {
    this.userForm = this.createUserFormGroup();
    this.isAdminCreatingUser = true;
    this.sortedFields = this.editFeatureService.sortFieldsByOrder(this.fields);
  }

  saveUser(): void {
    const formData = this.userForm?.value;
    this.isAdminCreatingUser = false;
    this.userForm = null;
    this.cancelList(false, false);
    this.editFeatureService.createNewUser(formData).subscribe((response) => {
      this.displayService.toastrShowSuccess({ message: this.selText.saveSuccessful });
      this.processUsers(response);
    },
      error => {
        console.error(this.selText.saveUnsuccessful + ": " + error);
        this.displayService.toastrShowError({ message: this.selText.saveUnsuccessful });
      });
  }

  deleteUser(username: string): void {
    this.editFeatureService.deleteUser(username).subscribe(() => { })
  }

  cancelUser(): void {
    this.isAdminCreatingUser = false;
    this.userForm = null;
    this.cancelList(false, false);
  }

  private usersSorted(): User[] {
    const usersInAllowedGroups = this.users.filter(user =>  // Only show users who belong to one of the allowed groups
      user.groups.some(group => this.allowedGroups.includes(group.name))
    );

    return usersInAllowedGroups.sort((a: User, b: User) => {  // Sort the filtered users in an alphabetic order
      const nameA = ((a.userUpdate.origin === 'local' ? a.username : a.userUpdate.name) || '').toLowerCase();
      const nameB = ((b.userUpdate.origin === 'local' ? b.username : b.userUpdate.name) || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  private nonFilteredUsersSorted(): User[] {
    return this.users.sort((a: User, b: User) => {  // Sort the filtered users in an alphabetic order
      const nameA = ((a.userUpdate.origin === 'local' ? a.username : a.userUpdate.name) || '').toLowerCase();
      const nameB = ((b.userUpdate.origin === 'local' ? b.username : b.userUpdate.name) || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  private getAllowedGroups(): void {
    this.allowedGroups.push(this.applicationSettings.option?.edit?.userConfigAccess?.read);
    this.allowedGroups.push(this.applicationSettings.option?.edit?.userConfigAccess?.update);
    this.allowedGroups.push(this.applicationSettings.option?.edit?.userConfigAccess?.admin);
  }

  private processUsers(response: any): void {
    this.users = [];
    this.allowedGroupsWithIds = [];
    const groupMap = new Map<number, string>(); // To store group names by their IDs
    response.groups.forEach((group: any) => {
      groupMap.set(group.id, group.name);
      this.allowedGroups.forEach(allowedGroup => {
        if (allowedGroup === group.name) {
          const allowedGroupWithId = {
            id: group.id,
            name: group.name,
            selected: false
          };
          this.allowedGroupsWithIds.push(allowedGroupWithId);
        }
      });
    });

    response.users.forEach((responseUser: any) => {
      const user: User = {
        id: responseUser.id,
        username: responseUser.username,
        groups: [],
        userUpdate: {
          date_created: responseUser.date_created,
          domain: responseUser.domain,
          email: responseUser.email,
          family_name: responseUser.family_name,
          given_name: responseUser.given_name,
          locale: responseUser.locale,
          name: responseUser.name,
          notat: responseUser.notat,
          origin: responseUser.origin,
          picture: responseUser.picture,
          rights: responseUser.rights,
          url: responseUser.url
        }
      };

      // Find groups for the current user using UserInGroup information
      response.UserInGroup.forEach((userInGroup: UserInGroup) => {
        if (userInGroup.user_id === responseUser.id) {
          const groupName = groupMap.get(userInGroup.group_id);
          if (groupName && this.allowedGroups.includes(groupName)) {
            const newGroup: UserGroupInfo = {
              id: userInGroup.group_id,
              name: groupName,
              selected: true
            };
            user.groups.push(newGroup);
          }
        }
      });

      this.users.push(user);
    });
    this.sortedUsers = this.usersSorted();
    this.sortedNonFilteredUsers = this.nonFilteredUsersSorted();
  }

  private getUsers(): void {
    this.editFeatureService.getUsers().subscribe({
      next: (response) => this.processUsers(response),
      error: () => this.displayService.toastrShowError({ message: this.selText.fetchDataError }),
    });
  }

  listUsers(): void {
    this.isAdminEditingUsers = true;

    this.users.forEach((user: User) => {
      const existingGroupIds = user.groups.map(group => group.id);

      this.allowedGroupsWithIds.forEach((allowedGroup: UserGroupInfo) => {
        if (!existingGroupIds.includes(allowedGroup.id)) {
          user.groups.push(allowedGroup);
        }
      });

      // Sort user.groups array by group id to ensure consistent ordering
      user.groups.sort((a: UserGroupInfo, b: UserGroupInfo) => {
        return a.id - b.id;
      });
    });
  }

  cancelList(isAdminEditingUsers: boolean, forceQuitFilteringUsers: boolean): void {
    this.isAdminEditingUsers = isAdminEditingUsers;
    if (forceQuitFilteringUsers) this.isFilteringUsers = false;
    this.selectedArea = null;
    this.isAdminEditingAreas = false;
    this.selectedUser = null;
    this.originalUser = null;
    this.editFeatureService.drawAreaSource?.clear();
    this.editFeatureService.removeInteractions([this.drawAreaTranslateInteraction, this.drawAreaModifyInteraction]);
    this.layoutService.updateMainActionViewWidthToInitState();
  }

  getFieldTitle(fieldName: string): string {
    const emailField = (this.applicationSettings?.option?.boards?.userConfig?.fields || []).find((field: any) => field.name === fieldName);
    return emailField ? emailField.title : fieldName;
  }

  disableUsersGroupCheckbox(group: UserGroupInfo): boolean {
    const adminGroup = this.applicationSettings.option?.edit?.userConfigAccess?.admin;
    if (!adminGroup) return false;
    if (this.userService.userAccess <= UserAccess.Admin && group.name === adminGroup) {
      return true;
    } else return false;
  }

  showUserDetails(user: User): void {
    this.newPasswordFieldType = 'password';
    this.hasUserChanged = false;
    this.selectedUser = structuredClone(user);
    this.originalUser = structuredClone(user);

    const allowedGroups = this.allowedGroupsWithIds ?? [];
    const groupIdSet = new Set(this.selectedUser.groups.map(g => g.id));

    // Only add missing allowed groups
    allowedGroups.forEach(allowed => {
      if (!groupIdSet.has(allowed.id)) {
        this.selectedUser!.groups.push({
          id: allowed.id,
          name: allowed.name,
          selected: false,
        });
      }
    });

    // Track selected group and ensure only one selected
    const previouslySelectedId = this.selectedUser.groups.find(g => g.selected)?.id;
    this.selectedUser.groups.forEach(g => g.selected = false);

    if (previouslySelectedId != null) {
      const match = this.selectedUser.groups.find(g => g.id === previouslySelectedId);
      if (match) match.selected = true;
    } else if (this.selectedUser.groups.length === 1) {
      this.selectedUser.groups[0].selected = true;
    }

    // Sort by id to ensure consistency in view
    this.selectedUser.groups.sort((a, b) => a.id - b.id);

    this.editFeatureService.getUserAreaSettings(user.id).subscribe({
      next: (settings) => {
        const config = settings?.features?.[0]?.properties?.config;
        if (!config) return;

        const defaultArea = config.key?.id ?? null;
        const otherAreas = config.extra?.otherAreas ?? [];

        this.selectedUser!.settingsId = settings.features[0].properties.id;
        this.selectedUser!.defaultArea = defaultArea;
        this.originalUser!.defaultArea = defaultArea;
        this.selectedUser!.otherAreas = [...otherAreas];
        this.originalUser!.otherAreas = [...otherAreas];

        this.changeDetector.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching user area settings:', err);
      }
    });
  }

  showAreaDetails(area: UserArea, skipZoomToFeature?: boolean): Feature<Geometry> {
    this.selectedArea = area;
    const selectedGeoArea: UserArea | undefined = this.editFeatureService.allAreas$.getValue().find((geoArea: any) => geoArea.geoJson.properties.id === area.id);
    const feature = new GeoJSON().readFeature(selectedGeoArea?.geoJson) as Feature<Geometry>;  // Convert GeoJSON feature to OpenLayers feature
    this.originalAreaGeometry = feature.getGeometry()?.clone() ?? null;

    const changeListenerKey = feature.on('change', () => {
      this.hasAreaChanged = true;
      unByKey(changeListenerKey);
      this.changeDetector.markForCheck();
    });

    this.editFeatureService.removeInteractions([this.drawAreaTranslateInteraction, this.drawAreaModifyInteraction]);
    this.editFeatureService.drawAreaSource?.clear();
    this.editFeatureService.drawAreaSource?.addFeature(feature);

    // Zoom to the extent of the selected area feature
    const geometry = feature.getGeometry();
    if (geometry && !skipZoomToFeature) {
      const extent: Extent = geometry.getExtent();
      this.editFeatureService.mapObject?.getView().fit(extent, { padding: [100, 100, 100, 100] });
    }

    return feature;
  }

  showAreaDetailsWithInteractions(area: UserArea, skipZoomToFeature?: boolean): void {
    this.hasAreaChanged = false;
    this.newAreaName = area.name;
    let feature: Feature | null = null;
    if (skipZoomToFeature) {
      feature = this.showAreaDetails(area, skipZoomToFeature);
    }
    else {
      feature = this.showAreaDetails(area);
    }

    this.drawAreaModifyInteraction = new Modify({
      features: new Collection([feature]),
      insertVertexCondition: () => true
    });
    this.editFeatureService.mapObject?.addInteraction(this.drawAreaModifyInteraction);

    this.drawAreaTranslateInteraction = new Translate({
      features: new Collection([feature])
    });
    this.editFeatureService.mapObject?.addInteraction(this.drawAreaTranslateInteraction);
  }

  onRestrictedAreaNameChange(value: string): void {
    if (value.trim() !== "") {
      this.hasAreaChanged = true;
    }
    else {
      this.hasAreaChanged = false;
    }
    this.newAreaName = value;
  }

  private isDefaultAreaValid(defaultArea: number): boolean {
    const numericDefaultArea = parseInt(defaultArea.toString());
    if (isNaN(numericDefaultArea)) return false;

    const selectedArea = this.editFeatureService.allAreas$.getValue().find((area: UserArea) => area.id === numericDefaultArea);
    if (!selectedArea) return false;

    return true;
  }

  private isEmailValid(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  saveUserChanges(user: User): void {
    this.hasUserChanged = false;
    const originalUser = structuredClone(this.originalUser);

    // Validate default area
    if (!user.defaultArea || !this.isDefaultAreaValid(user.defaultArea)) {
      this.displayService.toastrShowError({ message: this.selText.noDefaultAreaLabel });
      return;
    }
    else {
      user.defaultArea = parseInt(user.defaultArea.toString());
    }

    // Check for password mismatch
    const passwordMismatch = this.selectedUser?.userUpdate?.password !== this.selectedUser?.userUpdate?.confirmPassword;
    if (passwordMismatch) {
      this.displayService.toastrShowError({ message: this.selText.mismatchingPasswords });
      return;
    }

    // No need to send the confirmation password to jsonPayload
    this.selectedUser?.userUpdate?.confirmPassword === "";

    // Don't allow invalid email
    if (user.userUpdate.email && !this.isEmailValid(user.userUpdate.email)) {
      this.displayService.toastrShowError({ message: this.selText.illegalEmail });
      return;
    }

    user.otherAreas = user.otherAreas?.filter(otherArea => otherArea !== user.defaultArea); // Remove default area from other areas

    forkJoin({
      updateUserResponse: this.editFeatureService.updateUser(user, originalUser),
      updateUserSettingsResponse: this.editFeatureService.updateUserSettings(user, originalUser)
    }
    ).pipe(
      tap((response: any) => {
        if (response?.updateUserResponse) {
          this.processUsers(response.updateUserResponse);
        }
        else {
          this.getUsers();
        }
      })
    ).subscribe(
      () => {
        this.displayService.toastrShowSuccess({ message: this.selText.saveSuccessful });
      },
      (error) => {
        console.error(this.selText.saveUnsuccessful + ": " + error);
        this.displayService.toastrShowError({ message: this.selText.saveUnsuccessful });
      },
      () => {
        this.cancelList(false, false);
        this.listUsers();
        this.changeDetector.markForCheck();
      }
    );
  }

  resizeArea(event: Event, area: UserArea, expand: boolean): void {
    event.stopPropagation();

    let scaleFactorPercent = this.applicationSettings?.option?.edit?.restrictedArea?.resizeScalePercent ?? 1;

    if (scaleFactorPercent > 99 || scaleFactorPercent < 1) {
      scaleFactorPercent = 1;
    }

    const scaleFactor = expand ? 1 + scaleFactorPercent / 100 : 1 - scaleFactorPercent / 100;

    const olFeature = this.editFeatureService.drawAreaSource?.getFeatureById((area.geoJson as any).id);
    const olGeometry = olFeature?.getGeometry() as Polygon

    const centroid = olGeometry.getInteriorPoint().getCoordinates();
    const coordinates = olGeometry.getCoordinates()[0];

    const scaledCoordinates = coordinates.map(coord => [
      centroid[0] + scaleFactor * (coord[0] - centroid[0]),
      centroid[1] + scaleFactor * (coord[1] - centroid[1])
    ]);

    olGeometry.setCoordinates([scaledCoordinates]);
  }

  rotateArea(event: Event, area: UserArea, rotateClockwise: boolean): void {
    event.stopPropagation();

    const angleDegrees: number = this.applicationSettings.option?.edit?.restrictedArea?.rotateDegrees ?? 90;

    const olFeature = this.editFeatureService.drawAreaSource?.getFeatureById((area.geoJson as any).id);
    const olGeometry = olFeature?.getGeometry() as Polygon;

    if (!olGeometry) {
      console.error('No valid polygon geometry found.');
      return;
    }

    const centroid = olGeometry.getInteriorPoint().getCoordinates();
    const coordinates = olGeometry.getCoordinates()[0];

    let angleRadians = angleDegrees * (Math.PI / 180);  // Convert the angle from degrees to radians

    // Adjust the angle based on the rotation direction
    if (!rotateClockwise) {
      angleRadians = -angleRadians;
    }

    const rotatedCoordinates = coordinates.map(coord => {
      const dx = coord[0] - centroid[0];
      const dy = coord[1] - centroid[1];
      return [
        centroid[0] + (dx * Math.cos(angleRadians) - dy * Math.sin(angleRadians)), // x' = x * cos(angle) - y * sin(angle)
        centroid[1] + (dx * Math.sin(angleRadians) + dy * Math.cos(angleRadians))  // y' = x * sin(angle) + y * cos(angle)
      ];
    });

    olGeometry.setCoordinates([rotatedCoordinates]);
  }

  updateArea(event: Event, area: UserArea): void {
    event.stopPropagation();
    const feature = this.editFeatureService.drawAreaSource?.getFeatures()[0];
    const geometry = feature?.getGeometry();
    if (!geometry) {
      console.error("No geometry found to update for area: " + area.id);
      this.cancelList(false, false);
      this.displayService.toastrShowError({ message: this.selText.saveUnsuccessful });
      return;
    }

    const confirmed = window.confirm(this.selText.confirmSubmit);
    if (confirmed) {
      this.submitAreaModification(area, geometry);
    }
    else {
      this.showAreaDetailsWithInteractions(area, true);
    }
    this.hasAreaChanged = false;
  }

  private submitAreaModification(area: UserArea, geometry: Geometry): void {
    const application = this.applicationSettings.application;
    const action = "update";
    const table = this.editFeatureService.findTableNameFromSpecifiedBoard('areas');
    const idKey = this.applicationSettings.option.tables[table].id;

    const key = { [idKey]: area.id };

    const jsonPayload: {
      application: string;
      table: string;
      key: { [key: string]: any };
      action: string;
      values?: { [key: string]: any };
      geometry?: {
        srid: number;
        wkt: string;
      }
    } = { application, table, key, action };

    if (area.name !== this.newAreaName.trim()) {
      jsonPayload.values = {
        name: this.newAreaName
      }
    }

    const writer = new WKT();
    const areaWktGeometry = writer.writeGeometry(geometry);

    if (this.originalAreaGeometry) {
      const originalWktGeometry = writer.writeGeometry(this.originalAreaGeometry);
      if (areaWktGeometry === originalWktGeometry && area.name === this.newAreaName.trim()) {
        return; // Nothing has changed
      }
    }

    const isValidFeature = this.editFeatureService.isFeatureValid(areaWktGeometry);

    if (!isValidFeature) {
      this.displayService.toastrShowError({ message: this.selText.illegalGeometry });
      this.showAreaDetailsWithInteractions(area);
      return;
    }

    const srid = this.applicationSettings?.option?.map?.epsg;
    jsonPayload.geometry = {
      srid: srid,
      wkt: areaWktGeometry
    }

    const url: string = environment.editFeatureURL + action;

    if (!environment.production) {
      console.log(jsonPayload);
    }

    this.http.post(url, jsonPayload).pipe(
      switchMap(() => {
        return this.editFeatureService.getAndSetAllAreas();
      })
    ).subscribe(
      () => {
        this.displayService.toastrShowSuccess({ message: this.selText.saveSuccessful });
        this.showAreaDetailsWithInteractions(area, true);
      },
      (error) => {
        console.error(this.selText.saveUnsuccessful + ": " + error);
        this.displayService.toastrShowError({ message: this.selText.saveUnsuccessful });
        this.editFeatureService.cancelNewRestrictedArea();
      }
    );
  }

  private createUserFormGroup(): FormGroup {
    const formControls: { [key: string]: FormControl } = {};
    this.fields = this.applicationSettings?.option?.boards?.userConfig?.fields || [];
    this.fields.forEach((field: Field) => {
      const validators: ValidatorFn[] = [];
      let defaultValue = null;
      if (field.type === FieldTypes.Integer || field.type === FieldTypes.Double) {
        validators.push(Validators.pattern(/^-?(0|[1-9]\d*)([.,]\d+)?$/));
      }
      else if (field.type === FieldTypes.Email) {
        validators.push((control) => Validators.email(control));
      }
      else if (field.type === FieldTypes.Dropdown) {
        field.optionsObj?.forEach(obj => {
          if (obj.default) {
            defaultValue = obj.value;
          }
        });
      }
      else {
        validators.push(Validators.pattern(/\S/));  // Disallow a whitespace string
      }
      if (field.mandatory) {
        //validators.push(Validators.required);
        validators.push((control) => Validators.required(control));
      }
      formControls[field.name] = new FormControl(defaultValue, validators);
    });
    const formGroup = new FormGroup(formControls);
    return formGroup;
  }

  createNewRestrictedArea(): void {
    this.isAdminCreatingRestrictedArea = false;
    this.editFeatureService.isDrawingRestrictedArea$.next(false);
    this.editFeatureService.createNewRestrictedArea(this.restrictedAreaName);
    this.displayService.allowFeatureClick();
    this.restrictedAreaName = "";
  }

  cancelNewRestrictedArea(event: PointerEvent): void {
    this.restrictedAreaName = "";
    event.stopPropagation();
    this.isAdminCreatingRestrictedArea = false;
    this.editFeatureService.isDrawingRestrictedArea$.next(false);
    this.displayService.allowFeatureClick();
    this.editFeatureService.cancelNewRestrictedArea();
  }

  onAreaChange(event: Event, areaId: number): void {
    const inputElement = event.target as HTMLInputElement;
    if (this.selectedUser) {
      // Create otherAreas if it does not exist
      this.selectedUser.otherAreas = this.selectedUser.otherAreas || [];

      const isChecked = inputElement.checked;

      if (isChecked) {
        // Add area to selectedUser.otherAreas (if not already there)
        if (!this.selectedUser?.otherAreas?.includes(areaId)) {
          this.selectedUser?.otherAreas?.push(areaId);
        }
      }
      else {
        // Remove area from selectedUser.otherAreas
        const index = this.selectedUser?.otherAreas?.indexOf(areaId);
        if (index > -1) {
          this.selectedUser?.otherAreas?.splice(index, 1);
        }
      }
    }
  }

  onAreaSelectionChange(event: MatSelectChange): void {
    this.areasSubscription = this.editFeatureService.getSelectedArea(event.value).subscribe((data) => {
      const coordinates = data.features[0].geometry.coordinates;
      let minX = coordinates[0][0][0];
      let minY = coordinates[0][0][1];
      let maxX = coordinates[0][0][0];
      let maxY = coordinates[0][0][1];

      coordinates.forEach((polygon: any) => {
        polygon.forEach((point: any) => {
          const x = point[0];
          const y = point[1];

          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        });
      });

      this.editFeatureService.drawAreaSource?.clear();

      const feature = new Feature({
        geometry: new Polygon(coordinates)
      });
      this.editFeatureService.areaPolygon = feature;
      this.editFeatureService.setBufferedAreaPolygon();

      this.editFeatureService.drawAreaSource?.addFeature(feature);

      const mapSize = this.editFeatureService.mapObject?.getSize();

      const adjustedBounds = [
        minX - (maxX - minX) * 0.1, // Add a 10% buffer around the original bounds
        minY - (maxY - minY) * 0.1,
        maxX + (maxX - minX) * 0.1,
        maxY + (maxY - minY) * 0.1,
      ];

      // Use getZoomForExtent to determine the zoom level for the adjusted bounds
      const zoomLevel = this.editFeatureService.mapObject?.getView().getZoomForResolution(
        this.editFeatureService.mapObject.getView().getResolutionForExtent(adjustedBounds, mapSize)
      );

      const format = new GeoJSON();
      this.displayService.panAndZoomToFeature(format.readFeature(data.features[0]), zoomLevel ?? 1, 350);
    })
  }

  async cutFeature(): Promise<void> {
    const selectedFeature = this.editFeatureService.selectInteraction?.getFeatures()?.item(0);
    const areaFeature = this.editFeatureService.drawAreaSource?.getFeatures()?.[0];
    const selectedFeatureGeometry = selectedFeature?.getGeometry();
    const areaFeatureGeometry = areaFeature?.getGeometry();

    let selectedFeatureCoordinates: any = (selectedFeatureGeometry as LineString || MultiLineString)?.getCoordinates();
    const areaFeatureCoordinates: any = (areaFeatureGeometry as Polygon)?.getCoordinates();

    // Flatten the array if the selected feature is a MultiLineString
    if (Array.isArray(selectedFeatureCoordinates) && Array.isArray(selectedFeatureCoordinates[0])) {
      selectedFeatureCoordinates = selectedFeatureCoordinates[0];
    }

    const { multiLineString, polygon } = await import('@turf/helpers');
    const { default: lineIntersect } = await import('@turf/line-intersect');

    // Create Turf features from the selected and area features and calculate the intersection points
    const selectedTurfFeature = multiLineString([selectedFeatureCoordinates]);
    const areaTurfFeature = polygon(areaFeatureCoordinates);
    const intersections = lineIntersect(selectedTurfFeature, areaTurfFeature);

    let intersectionCoords = intersections.features.map((feat) => feat.geometry.coordinates);
    if (intersectionCoords.length < 1) return;

    // Sort the intersection points by their distance along the line
    intersectionCoords = intersectionCoords.sort((a, b) => {
      const distA = this.getDistanceAlongLine(a as [number, number], selectedFeatureCoordinates);
      const distB = this.getDistanceAlongLine(b as [number, number], selectedFeatureCoordinates);
      return distA - distB;
    });

    // Create new features by cutting the selected feature at the intersection points
    const cutSegments: any[] = [];
    let startIndex = 0;

    intersectionCoords.forEach((point) => {
      let minDist = Infinity;
      let insertIndex = -1;

      // Find the closest segment
      for (let i = startIndex; i < selectedFeatureCoordinates.length - 1; i++) {
        const segStart = selectedFeatureCoordinates[i];
        const segEnd = selectedFeatureCoordinates[i + 1];

        // Calculate distance from point to the segment
        const dist = this.pointToSegmentDistance(point, segStart, segEnd);
        if (dist < minDist) {
          minDist = dist;
          insertIndex = i + 1;
        }
      }

      // Insert the intersection point into the array
      if (insertIndex !== -1) {
        selectedFeatureCoordinates.splice(insertIndex, 0, point);
      }

      // Create a new feature segment from the startIndex to insertIndex
      const segmentCoordinates = selectedFeatureCoordinates.slice(startIndex, insertIndex + 1);
      cutSegments.push(segmentCoordinates);
      startIndex = insertIndex;
    });

    // After the last intersection, add the remaining part of the line
    if (startIndex < selectedFeatureCoordinates.length - 1) {
      const remainingSegment = selectedFeatureCoordinates.slice(startIndex);
      cutSegments.push(remainingSegment);
    }

    // Create new features from the segments and add necessary properties
    const newFeatures = cutSegments.map((segmentCoords) => {
      const newFeatureGeometry = new LineString(segmentCoords);
      const newFeature = new Feature(newFeatureGeometry);
      newFeature.set('isDragAndDrop', true);
      return newFeature;
    });

    // Remove the selected feature from the map and add the new features
    if (selectedFeature) this.layerService.getDragAndDropDrawingLayer()?.getSource()?.removeFeature(selectedFeature);
    this.layerService.getDragAndDropDrawingLayer()?.getSource()?.addFeatures(newFeatures);
    //this.displayService.setMainActionViewActive(false);
  }

  // Helper function to calculate the distance between two points
  getDistanceBetweenPoints(pointA: [number, number], pointB: [number, number]): number {
    const [x1, y1] = pointA;
    const [x2, y2] = pointB;
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  }

  // Helper function to calculate the distance along the line from the start to a given point
  getDistanceAlongLine(point: [number, number], lineCoordinates: [number, number][]): number {
    let distance = 0;
    for (let i = 0; i < lineCoordinates.length - 1; i++) {
      const startPoint = lineCoordinates[i];
      const endPoint = lineCoordinates[i + 1];

      // Calculate the distance from the start of the segment to the point
      const segmentDistance = this.getDistanceBetweenPoints(startPoint, endPoint);
      const pointDistance = this.getDistanceBetweenPoints(startPoint, point);

      // Check if the point lies within the current segment
      const totalSegmentDistance = this.getDistanceBetweenPoints(startPoint, endPoint);
      if (pointDistance < totalSegmentDistance) {
        distance += pointDistance;
        break;
      } else {
        distance += segmentDistance;
      }
    }
    return distance;
  }

  // Function to calculate the perpendicular distance from a point to a line segment
  pointToSegmentDistance(point: number[], segStart: number[], segEnd: number[]): number {
    const [px, py] = point;
    const [x1, y1] = segStart;
    const [x2, y2] = segEnd;

    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    const param = lenSq !== 0 ? dot / lenSq : -1;

    let xx, yy;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  onAlterAreaClick(): void {
    this.isAdminEditingAreas = true;
    this.layoutService.updateMainActionViewWidth(0);
  }

}